import type { ContentId_ACTIONABLE, ContentId_MIAB, ContentId_SUMMARY, ContentIxStatus_ACTIONABLE, ContentIxStatus_MIAB, ContentIxStatus_SUMMARY, ContentMetadata, SkillPageContentProps } from "@newgenerated/shared/schema";
import { useGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import { getInitialSuspendTranslationFns, type TranslationFnsAsync } from "@/components/cards/ContentCard";
import { createStore, type Store } from "@/common/storeUtils";
import { GaTabs, TabContent, type TabsOption } from "@/components/general/GaTab";
import { ContentMetadataGridView, type ContentMetadataViewExtended, convertContentMetadataView } from "@/components/content/ContentMetadataView";
import { type DeepReadonly, defineComponent, type PropType } from "vue";
import { createSynchronizedQueue } from "@utils/requestUtils";
import { getContent, getIxStatus } from "@newgenerated/shared/stubs";
import { convertToActionableFiltersForm, convertToMiabFiltersForm, convertToSummaryFiltersForm, convertToSummarySort } from "@/components/sorting-filtering-paging/ContentSortingFiltering";
import { toggleSummaryBookmark } from "@generated/api/summaryIxBookmarkControllerApi";
import type { SkillPageContentContext } from "@generated-widgets/DefaultWidgets";

type ContentTabType = "ALL" | "SUMMARY" | "ACTIONABLE" | "MIAB";

type SkillPageContentState = {
  activeTab: ContentTabType;
  translationFns: TranslationFnsAsync;
  content: ContentMetadataViewExtended;
  ixStatus: {
    summary: Map<ContentId_SUMMARY["id"], ContentIxStatus_SUMMARY>;
    miab: Map<ContentId_MIAB["id"], ContentIxStatus_MIAB>;
    actionable: Map<ContentId_ACTIONABLE["id"], ContentIxStatus_ACTIONABLE>;
  };
  status: "LOADING" | "READY" | "ERROR";
};

const initStoreHandler = (props: SkillPageContentProps): { store: Store<SkillPageContentState>; applyFilters: () => void; onBookmark: (content: DeepReadonly<ContentMetadata>) => void } => {
  const store = createStore<SkillPageContentState>({
    content: convertContentMetadataView(props.content),
    activeTab: "ALL",
    translationFns: getInitialSuspendTranslationFns(),
    status: "READY",
    ixStatus: {
      summary: new Map(),
      miab: new Map(),
      actionable: new Map(),
    },
  });

  const queue = createSynchronizedQueue();
  const applyFilters = (): void => {
    queue.run(async (): Promise<void> => {
      store.sub("status").set("LOADING");
      const result = await getContent({
        body: {
          actionable: {
            filter: convertToActionableFiltersForm(store.sub("content").sub("actionable").sub("filter").sub("formFilters").get()),
            pageInfo: store.sub("content").sub("actionable").sub("pageInfo").get(),
          },
          miab: {
            filter: convertToMiabFiltersForm(store.sub("content").sub("miab").sub("filter").sub("formFilters").get()),
            pageInfo: store.sub("content").sub("miab").sub("pageInfo").get(),
          },
          summary: {
            filter: convertToSummaryFiltersForm(store.sub("content").sub("summary").sub("filter").sub("formFilters").get()),
            pageInfo: store.sub("content").sub("summary").sub("pageInfo").get(),
            sorting: convertToSummarySort(store.sub("content").sub("summary").sub("sorting").get()),
          },
          tag: props.tag,
        },
      });
      if (result.kind === "success") {
        store.sub("content").set(convertContentMetadataView(result.data));
        store.sub("status").set("READY");
      } else {
        store.sub("status").set("ERROR");
      }
    });
  };

  const fetchIxStatus = (contentMetadatas: DeepReadonly<ContentMetadata[]>): void => {
    queue.run(async (): Promise<void> => {
      const response = await getIxStatus({
        body: {
          items: contentMetadatas.map((metadata) => metadata.contentId),
        },
      });
      //only handle success case
      if (response.kind === "success") {
        store.sub("ixStatus").set({
          summary: new Map(response.data.items.filter((item) => item.contentType === "SUMMARY").map((item) => [item.contentId.id, item])),
          miab: new Map(response.data.items.filter((item) => item.contentType === "MIAB").map((item) => [item.contentId.id, item])),
          actionable: new Map(response.data.items.filter((item) => item.contentType === "ACTIONABLE").map((item) => [item.contentId.id, item])),
        });
      }
    });
  };

  const onBookmark = (content: DeepReadonly<ContentMetadata>): void => {
    queue.run(async (): Promise<void> => {
      if (content.contentType === "SUMMARY") {
        try {
          void (await toggleSummaryBookmark(content.dataId));
          const metadatas = store.sub("content").get();
          fetchIxStatus([...metadatas.actionable.items, ...metadatas.miab.items, ...metadatas.summary.items]);
        } catch {
          //do nothing
        }
      }
    });
  };

  fetchIxStatus([...props.content.summary.items, ...props.content.actionable.items, ...props.content.miab.items]);

  return {
    store: store,
    applyFilters: applyFilters,
    onBookmark: onBookmark,
  };
};

export const SkillPageContent = defineComponent({
  props: {
    skillPageContentProps: {
      type: Object as PropType<SkillPageContentProps>,
      required: true,
    },
    context: {
      type: Object as PropType<SkillPageContentContext>,
      required: true,
    },
  },
  setup(props) {
    const { currentLanguage } = useGaContext();
    const { t } = props.context;

    const stateHandler = initStoreHandler(props.skillPageContentProps);

    const actionableTab: TabsOption<ContentTabType>[] = props.skillPageContentProps.hasActionableFeature ? [{ value: "ACTIONABLE", label: t("general:actionables", "Actionables") }] : [];
    const contentTabsOptions: TabsOption<ContentTabType>[] = [
      { value: "ALL", label: t("general:content.topResults", "Top Results") },
      { value: "SUMMARY", label: t("general:summaries", "Summaries") },
      ...actionableTab,
      { value: "MIAB", label: t("general:miab", "Meeting in a Box") },
    ];

    return () => (
      <div>
        <GaTabs tabOptions={contentTabsOptions} store={stateHandler.store.sub("activeTab")} />
        <TabContent active={stateHandler.store.sub("activeTab").get() === "ALL"}>
          {props.skillPageContentProps.content.actionable.items.length === 0 || !props.skillPageContentProps.hasActionableFeature ? null : (
            <>
              <h2 class="mb-3 mt-5">{t("general:actionables")}</h2>
              <ContentMetadataGridView
                trackingParams={new URLSearchParams(props.skillPageContentProps.tracking.actionableParams)}
                status={stateHandler.store.sub("status").get()}
                translationFn={stateHandler.store.sub("translationFns").sub(currentLanguage())}
                items={props.skillPageContentProps.content.actionable.items.slice(0, 4)}
                onBookmark={stateHandler.onBookmark}
                ixStatus={stateHandler.store.sub("ixStatus").sub("actionable").get()}
              />
            </>
          )}
          {props.skillPageContentProps.content.miab.items.length === 0 ? null : (
            <>
              <h2 class="mb-3 mt-5">{t("miab:overviewPage.title")}</h2>
              <ContentMetadataGridView
                trackingParams={new URLSearchParams(props.skillPageContentProps.tracking.miabParams)}
                status={stateHandler.store.sub("status").get()}
                translationFn={stateHandler.store.sub("translationFns").sub(currentLanguage())}
                items={props.skillPageContentProps.content.miab.items.slice(Math.min(4, Math.max(props.skillPageContentProps.content.miab.items.length - 4, 0)), 8)}
                onBookmark={stateHandler.onBookmark}
                ixStatus={stateHandler.store.sub("ixStatus").sub("miab").get()}
              />
            </>
          )}
          {props.skillPageContentProps.content.summary.items.length === 0 ? null : (
            <>
              <h2 class="mb-3 mt-5">{t("general:summaries")}</h2>
              <ContentMetadataGridView
                trackingParams={new URLSearchParams(props.skillPageContentProps.tracking.summaryParams)}
                status={stateHandler.store.sub("status").get()}
                translationFn={stateHandler.store.sub("translationFns").sub(currentLanguage())}
                items={props.skillPageContentProps.content.summary.items.slice(0, 4)}
                onBookmark={stateHandler.onBookmark}
                ixStatus={stateHandler.store.sub("ixStatus").sub("summary").get()}
              />
            </>
          )}
        </TabContent>
        <TabContent active={stateHandler.store.sub("activeTab").get() === "SUMMARY"}>
          <ContentMetadataGridView
            trackingParams={new URLSearchParams(props.skillPageContentProps.tracking.summaryParams)}
            status={stateHandler.store.sub("status").get()}
            filteringAndSorting={{
              filterStore: stateHandler.store.sub("content").sub("summary").sub("filter"),
              sortingStore: stateHandler.store.sub("content").sub("summary").sub("sorting"),
              pageInfo: stateHandler.store.sub("content").sub("summary").sub("pageInfo"),
              apply: stateHandler.applyFilters,
            }}
            onBookmark={stateHandler.onBookmark}
            translationFn={stateHandler.store.sub("translationFns").sub(currentLanguage())}
            items={stateHandler.store.sub("content").sub("summary").sub("items").get()}
            ixStatus={stateHandler.store.sub("ixStatus").sub("summary").get()}
          />
        </TabContent>
        <TabContent active={stateHandler.store.sub("activeTab").get() === "ACTIONABLE"}>
          <ContentMetadataGridView
            trackingParams={new URLSearchParams(props.skillPageContentProps.tracking.actionableParams)}
            status={stateHandler.store.sub("status").get()}
            filteringAndSorting={{
              filterStore: stateHandler.store.sub("content").sub("actionable").sub("filter"),
              pageInfo: stateHandler.store.sub("content").sub("actionable").sub("pageInfo"),
              apply: stateHandler.applyFilters,
            }}
            translationFn={stateHandler.store.sub("translationFns").sub(currentLanguage())}
            items={stateHandler.store.sub("content").sub("actionable").sub("items").get()}
            onBookmark={stateHandler.onBookmark}
            ixStatus={stateHandler.store.sub("ixStatus").sub("actionable").get()}
          />
        </TabContent>
        <TabContent active={stateHandler.store.sub("activeTab").get() === "MIAB"}>
          <ContentMetadataGridView
            trackingParams={new URLSearchParams(props.skillPageContentProps.tracking.miabParams)}
            status={stateHandler.store.sub("status").get()}
            filteringAndSorting={{
              filterStore: stateHandler.store.sub("content").sub("miab").sub("filter"),
              pageInfo: stateHandler.store.sub("content").sub("miab").sub("pageInfo"),
              apply: stateHandler.applyFilters,
            }}
            translationFn={stateHandler.store.sub("translationFns").sub(currentLanguage())}
            items={stateHandler.store.sub("content").sub("miab").sub("items").get()}
            onBookmark={stateHandler.onBookmark}
            ixStatus={stateHandler.store.sub("ixStatus").sub("miab").get()}
          />
        </TabContent>
      </div>
    );
  },
});
